import PropTypes from 'prop-types';
import { normalizePhotoshopLightAngle } from './photoshopLightAngle';
import styled from 'styled-components';
import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';

const Dial = styled.button`
  box-sizing: border-box;
  width: 72px;
  height: 72px;
  position: relative;
  padding: 0;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.divider.primary};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.bg.tertiary};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 1px 3px rgba(0, 0, 0, 0.28);
  cursor: grab;
  touch-action: none;
  user-select: none;
  &:active {
    cursor: grabbing;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 3px;
  }
`;
const Indicator = styled.span`
  position: absolute;
  inset: 0;
  transform-origin: center;
  pointer-events: none;
  &::before {
    content: '';
    position: absolute;
    width: 3px;
    height: 15px;
    top: 4px;
    left: 50%;
    border-radius: 2px;
    background: ${({ theme }) => theme.colors.fg.linkNormal};
    transform: translateX(-50%);
  }
`;
const Value = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${({ theme }) => theme.colors.fg.primary};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
`;
const wrap = normalizePhotoshopLightAngle;

const LightDirectionDial = forwardRef(function LightDirectionDial(
  { value = null, onChange, onPreviewChange = onChange },
  ref
) {
  const pointer = useRef(null);
  const draft = useRef(null);
  const node = useRef(null);
  const indicator = useRef(null);
  const label = useRef(null);
  const bounds = useRef(null);
  const mixed = value === null;
  const angle = mixed ? 0 : value;
  const paint = (next) => {
    const isMixed = next === null;
    indicator.current.style.transform = `rotate(${90 - (next ?? 0)}deg)`;
    indicator.current.style.visibility = isMixed ? 'hidden' : 'visible';
    label.current.textContent = isMixed ? '—' : `${next}°`;
    node.current.setAttribute('aria-valuenow', next ?? 0);
    node.current.setAttribute(
      'aria-valuetext',
      isMixed ? __('Multiple directions', 'web-stories') : `${next}°`
    );
  };
  useLayoutEffect(() => {
    if (pointer.current === null) paint(value);
  });
  const updateFromPointer = (event) => {
    const rect = bounds.current;
    const dx = event.clientX - rect.left - rect.width / 2;
    const dy = event.clientY - rect.top - rect.height / 2;
    // Direction is undefined at the centre; retain the current angle there.
    if (Math.hypot(dx, dy) < 3) return;
    const next = wrap(Math.round((Math.atan2(-dy, dx) * 180) / Math.PI));
    if (draft.current === next) return;
    draft.current = next;
    // Painting this small control must not wait for a React/editor render.
    paint(next);
    onPreviewChange(next);
  };
  const finishPointer = (event) => {
    if (pointer.current !== event.pointerId) return;
    const next = draft.current;
    pointer.current = null;
    draft.current = null;
    bounds.current = null;
    indicator.current.style.willChange = '';
    if (next !== null) onChange(next);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };
  useImperativeHandle(ref, () => ({
    finish: () => {
      if (pointer.current !== null)
        finishPointer({
          pointerId: pointer.current,
          currentTarget: node.current,
        });
    },
  }));
  return (
    <Dial
      ref={node}
      type="button"
      role="slider"
      tabIndex={0}
      aria-label={__('Light direction dial', 'web-stories')}
      aria-valuemin={-180}
      aria-valuemax={180}
      aria-valuenow={angle}
      aria-valuetext={
        mixed ? __('Multiple directions', 'web-stories') : `${angle}°`
      }
      title={__(
        'Photoshop light angle: 0° is right; 90° is above; positive angles turn counterclockwise. Use arrow keys to adjust, or Shift for 15° steps.',
        'web-stories'
      )}
      onPointerDown={(event) => {
        if (!event.isPrimary || event.button !== 0 || pointer.current !== null)
          return;
        event.preventDefault();
        event.currentTarget.focus();
        pointer.current = event.pointerId;
        bounds.current = event.currentTarget.getBoundingClientRect();
        indicator.current.style.willChange = 'transform';
        event.currentTarget.setPointerCapture(event.pointerId);
        updateFromPointer(event);
      }}
      onPointerMove={(event) => {
        if (pointer.current === event.pointerId) updateFromPointer(event);
      }}
      onPointerUp={(event) => {
        if (pointer.current === event.pointerId) updateFromPointer(event);
        finishPointer(event);
      }}
      onPointerCancel={finishPointer}
      onLostPointerCapture={finishPointer}
      onBlur={(event) => {
        if (pointer.current !== null)
          finishPointer({
            pointerId: pointer.current,
            currentTarget: event.currentTarget,
          });
      }}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 15 : 1;
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
            ? 180
            : ['ArrowRight', 'ArrowUp'].includes(event.key)
            ? wrap((draft.current ?? angle) + step)
            : ['ArrowLeft', 'ArrowDown'].includes(event.key)
            ? wrap((draft.current ?? angle) - step)
            : null;
        if (next === null) return;
        event.preventDefault();
        event.stopPropagation();
        paint(next);
        if (pointer.current !== null) {
          draft.current = next;
          finishPointer({
            pointerId: pointer.current,
            currentTarget: event.currentTarget,
          });
        } else onChange(next);
      }}
    >
      <Indicator
        ref={indicator}
        aria-hidden="true"
        style={{
          transform: `rotate(${90 - angle}deg)`,
          visibility: mixed ? 'hidden' : 'visible',
        }}
      />
      <Value ref={label} aria-hidden="true">
        {mixed ? '—' : `${angle}°`}
      </Value>
    </Dial>
  );
});
LightDirectionDial.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onPreviewChange: PropTypes.func,
};

export default LightDirectionDial;
